import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@hooks/useAuth';
import { SW_MESSAGE } from '@lib/pwa/sw-messages';
import { invalidateChatCaches } from '@lib/chatCacheInvalidation';

/**
 * Refreshes chat caches when the ROOT service worker relays a CHAT_MESSAGE_RECEIVED
 * message on the OneSignal owner path.
 *
 * During the migration dual-send overlap a device can still be legacy-VAPID
 * subscribed until its teardown completes, so legacy chat pushes keep arriving.
 * When one lands while the user is viewing that DM, the root Workbox SW suppresses
 * the banner and postMessages CHAT_MESSAGE_RECEIVED. On the legacy path
 * `useWebPushNotifications` listens for that message; on the owner path it is NOT
 * mounted, and `useOneSignalNotificationEvents` only handles OneSignal's OWN
 * `foregroundWillDisplay`. Without this hook the suppressed legacy chat push would
 * update nothing inline (a regression vs the legacy path). Mounted ONLY on the
 * owner path (OneSignalPushInitializer); the legacy path keeps its own inline
 * branch untouched.
 *
 * Transport-agnostic, like `useSwNotificationNavigate`: it listens for the SW
 * message regardless of which pipeline delivered the push.
 *
 * `queryClient` is stable; `userId` is read through a ref so the message-listener
 * effect can run once (empty deps) without re-subscribing when the user changes.
 * The ref is synced via useEffect (not assigned during render) to satisfy Rules
 * of React / the React Compiler — mirrors useOneSignalNotificationEvents.
 */
export function useSwChatMessageRefresh(): void {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const userId = user?.id;
  const userIdRef = useRef(userId);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const handleMessage = (event: MessageEvent) => {
      const payload = event.data as { type?: string } | undefined;
      if (payload?.type !== SW_MESSAGE.CHAT_MESSAGE_RECEIVED) return;
      // Same trio the legacy CHAT_MESSAGE_RECEIVED branch invalidates, via the
      // shared helper. userId via ref to avoid a stale capture.
      void invalidateChatCaches(queryClient, userIdRef.current);
    };

    navigator.serviceWorker.addEventListener('message', handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleMessage);
    };
  }, [queryClient]);
}
